"use client";

import { useState, useEffect, useRef } from "react";
import {
  Search,
  Filter,
  Download,
  X,
  ArrowUpDown,
} from "lucide-react";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { workflowStages, salesProjects } from "@/assets/mock";
import type { WorkflowStage, SalesProject } from "@/types";
import ProjectPreview from "./ProjectPreview";
import { Tabs } from "../ui/tabs";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../ui/pagination";

const Stages = () => {
  const [selectedStage, setSelectedStage] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [previewProject, setPreviewProject] = useState<SalesProject | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [showStages, setShowStages] = useState(true);
  const [activeTab, setActiveTab] = useState<string>("all-stages");
  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setPreviewProject(null);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && previewProject) {
        setPreviewProject(null);
      }
    };

    if (previewProject) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [previewProject]);

  // Get display value for project name - show "Not Set" for Stage 1 leads
  const getDisplayName = (project: SalesProject) => {
    if (project.current_stage === 1 && (!project.name || project.name.trim() === "")) {
      return "Not Set";
    }
    return project.name || "Not Set";
  };

  // Get display value for other fields
  const getDisplayValue = (value: unknown) => {
    if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0)) {
      return "Not Set";
    }
    return value;
  };

  const filteredProjects = salesProjects.filter((project) => {
    const tabStageFilter = activeTab === "all-stages" 
      ? true 
      : project.current_stage === parseInt(activeTab.split('-')[1]);
    
    const matchesStage = selectedStage === null || project.current_stage === selectedStage;
    const matchesStatus = selectedStatus === "all" || project.status === selectedStatus;
    const matchesType = selectedType === "all" || project.type === selectedType;
    
    // Search in both project name and client name
    const displayName = getDisplayName(project).toLowerCase();
    const clientName = getDisplayValue(project.client).toLowerCase();
    const searchLower = searchQuery.toLowerCase();
    
    const matchesSearch =
      searchQuery === "" ||
      displayName.includes(searchLower) ||
      clientName.includes(searchLower);

    return tabStageFilter && matchesStage && matchesStatus && matchesType && matchesSearch;
  });

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    if (!sortConfig) return 0;

    const { key, direction } = sortConfig;
    
    // Handle special sorting for "Not Set" values
    let aValue = a[key as keyof SalesProject];
    let bValue = b[key as keyof SalesProject];

    // For Stage 1 projects with no name, treat "Not Set" as empty string for sorting
    if (key === 'name') {
      aValue = getDisplayName(a);
      bValue = getDisplayName(b);
    }

    if (key === 'progress' || key === 'current_stage') {
      aValue = Number(aValue);
      bValue = Number(bValue);
    } else {
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
    }

    // Handle "Not Set" values in sorting
    if (aValue === "not set" && bValue !== "not set") return direction === 'asc' ? -1 : 1;
    if (aValue !== "not set" && bValue === "not set") return direction === 'asc' ? 1 : -1;
    if (aValue === "not set" && bValue === "not set") return 0;

    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProjects = sortedProjects.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const clearFilters = () => {
    setSelectedStage(null);
    setSelectedStatus("all");
    setSelectedType("all");
    setSearchQuery("");
    setSortConfig(null);
    setCurrentPage(1);
    setActiveTab("all-stages");
  };

  const activeFiltersCount = [
    selectedStage !== null,
    selectedStatus !== "all",
    selectedType !== "all",
    searchQuery !== "",
    activeTab !== "all-stages",
  ].filter(Boolean).length;

  const getStageColor = (stageNumber: number) => {
    const stage = workflowStages.find((s) => s.stage_number === stageNumber);
    return stage?.color || "bg-gray-500";
  };

  const getStageName = (stageNumber: number) => {
    const stage = workflowStages.find((s) => s.stage_number === stageNumber);
    return stage?.name || "Unknown Stage";
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text === "Not Set") return text;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getSortIcon = (key: string) => {
    if (sortConfig?.key !== key) return <ArrowUpDown className="ml-2 h-3 w-3 text-gray-400" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUpDown className="ml-2 h-3 w-3 text-gray-600 rotate-180" />
      : <ArrowUpDown className="ml-2 h-3 w-3 text-gray-600" />;
  };

  const exportToExcel = () => {
    const headers = ['Project Name', 'Client', 'Stage', 'Progress', 'Value', 'Status', 'Completion Date'];
    const csvContent = [
      headers.join(','),
      ...sortedProjects.map(project => [
        `"${getDisplayName(project)}"`,
        `"${getDisplayValue(project.client)}"`,
        `"Stage ${project.current_stage}: ${getStageName(project.current_stage)}"`,
        `"${project.progress}%"`,
        `"${getDisplayValue(project.value)}"`,
        `"${project.status.replace('_', ' ').toUpperCase()}"`,
        `"${getDisplayValue(project.expected_completion)}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `projects-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTabChange = (tab: any) => {
    setActiveTab(tab.value);
    setCurrentPage(1);
  };

  const stageTabs = [
    {
      title: "All Stages",
      value: "all-stages",
      content: <div />,
    },
    ...workflowStages.map((stage) => ({
      title: `Stage ${stage.stage_number}`,
      value: `stage-${stage.stage_number}`,
      content: <div />,
    })),
  ];

  const getStagesToDisplay = () => {
    if (activeTab === "all-stages") {
      return workflowStages;
    } else {
      const stageNumber = parseInt(activeTab.split('-')[1]);
      return workflowStages.filter(stage => stage.stage_number === stageNumber);
    }
  };

  const stagesToDisplay = getStagesToDisplay();

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-[1800px] mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-lg font-bold text-gray-900 mb-1">Workflow Stages</h1>
            <p className="text-xs text-secondary">
              Complete Sales-Ops-Management collaboration process with {filteredProjects.length} projects & leads
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-white uppercase border border-secondary text-gray-700 font-normal rounded-lg text-xs transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filters
              {activeFiltersCount > 0 && (
                <span className="bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            <button 
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/90 text-white font-normal rounded-lg text-xs uppercase transition-colors"
            >
              <Download className="w-4 h-4" />
              Export Table Data as Excel
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-secondary/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold text-gray-900">Filter Projects & Leads</h3>
            <div className="flex items-center gap-2">
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-xs font-semibold text-primary hover:text-primary/80 flex items-center gap-1"
                >
                  <X className="w-3 h-3" />
                  Clear all
                </button>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="text-xs font-semibold text-gray-600 hover:text-gray-800"
              >
                {showFilters ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-2 block">Search</label>
                <input
                  type="text"
                  placeholder="Project name, client, or 'Not Set'..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-secondary/30 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-2 block">Stage</label>
                <select
                  value={selectedStage === null ? "" : selectedStage}
                  onChange={(e) =>
                    setSelectedStage(e.target.value === "" ? null : Number(e.target.value))
                  }
                  className="w-full px-3 py-2 border border-secondary/30 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">All Stages</option>
                  {workflowStages.map((stage) => (
                    <option key={stage.stage_number} value={stage.stage_number}>
                      Stage {stage.stage_number}: {stage.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-2 block">Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-secondary/30 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="on_track">On Track</option>
                  <option value="at_risk">At Risk</option>
                  <option value="delayed">Delayed</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-2 block">Project Type</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full px-3 py-2 border border-secondary/30 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="talent_placement">Talent Placement</option>
                  <option value="app_deployment">App Deployment</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold text-gray-900">Workflow Stages</h3>
            <button
              onClick={() => setShowStages(!showStages)}
              className="text-xs font-semibold text-primary hover:text-primary/80"
            >
              {showStages ? 'Hide Stage Details' : 'Show Stage Details'}
            </button>
          </div>

          {showStages && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {stagesToDisplay.map((stage: WorkflowStage) => {
                const projectsInStage = salesProjects.filter(
                  (p) => p.current_stage === stage.stage_number
                ).length;

                const isActiveStage = activeTab === `stage-${stage.stage_number}`;

                return (
                  <div
                    key={stage.id}
                    className={`
                      bg-gray-50 rounded-lg p-4 border border-secondary/20
                      ${isActiveStage ? 'col-span-full' : ''}
                    `}
                  >
                    <div className="flex-1">
                      <div className="flex justify-between items-center gap-2 mb-1">
                        <h3 className="text-xs font-bold text-gray-900">Stage {stage.stage_number}</h3>
                        <span className="text-xs font-semibold text-primary uppercase">
                          {projectsInStage} {stage.stage_number === 1 ? 'leads' : 'projects'}
                        </span>
                      </div>
                      <p className="text-xs text-secondary">{stage.timeline}</p>
                    </div>

                    <h4 className="text-xs font-bold text-gray-900 mb-2">{stage.name}</h4>
                    <p className="text-xs text-secondary mb-3">{stage.description}</p>

                    <div className="border-t border-secondary/20 pt-3">
                      <p className="text-xs text-secondary mb-1">
                        <span className="font-semibold text-gray-900">Owner:</span> {stage.owner}
                      </p>
                      <p className="text-xs text-secondary">
                        <span className="font-semibold text-gray-900">Rationale:</span> {stage.rationale}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="[perspective:1000px] relative">
            <Tabs 
              tabs={stageTabs}
              containerClassName="justify-start"
              activeTabClassName="bg-secondary text-white"
              tabClassName="px-4 py-2"
              onTabChange={handleTabChange}
            />
          </div>
        </div>

        <div className="bg-white border border-secondary rounded-r-xl rounded-l-xl overflow-hidden">
          <div className="p-5 border-b border-secondary/20 bg-secondary">
            <h3 className="text-xs font-bold text-white">
              {activeTab === "all-stages" 
                ? "All Projects & Leads" 
                : `Stage ${activeTab.split('-')[1]} ${parseInt(activeTab.split('-')[1]) === 1 ? 'Leads' : 'Projects'} - ${getStageName(parseInt(activeTab.split('-')[1]))}`}
            </h3>
            <p className="text-xs text-white mt-1">
              {filteredProjects.length} {activeTab === "all-stages" ? 'items' : (parseInt(activeTab.split('-')[1]) === 1 ? 'leads' : 'projects')} found
            </p>
          </div>

          <div className="overflow-x-auto custom-scrollbar">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center hover:text-gray-700 transition-colors"
                    >
                      Project Name
                      {getSortIcon('name')}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('client')}
                      className="flex items-center hover:text-gray-700 transition-colors"
                    >
                      Client
                      {getSortIcon('client')}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('current_stage')}
                      className="flex items-center hover:text-gray-700 transition-colors"
                    >
                      Stage
                      {getSortIcon('current_stage')}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedProjects.length > 0 ? (
                  paginatedProjects.map((project) => (
                    <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div 
                          className={`text-xs font-semibold ${getDisplayName(project) === "Not Set" ? "text-gray-400 italic" : "text-gray-900"}`}
                          title={getDisplayName(project)}
                        >
                          {truncateText(getDisplayName(project), 30)}
                        </div>
                        {project.current_stage === 1 && getDisplayName(project) === "Not Set" && (
                          <div className="text-xs text-gray-500 mt-1">(Lead - Name pending approval)</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div 
                          className={`text-xs ${getDisplayValue(project.client) === "Not Set" ? "text-gray-400 italic" : "text-secondary"}`}
                          title={getDisplayValue(project.client)}
                        >
                          {truncateText(getDisplayValue(project.client), 25)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${getStageColor(project.current_stage)}`} />
                            <span className="text-xs text-secondary">Stage {project.current_stage}</span>
                          </div>
                          <div className="text-xs text-secondary mt-0.5">
                            {truncateText(getStageName(project.current_stage), 20)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-xs text-secondary capitalize">
                          {project.type?.replace('_', ' ') || "Not Set"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          project.status === 'on_track' ? 'bg-green-100 text-green-800' :
                          project.status === 'at_risk' ? 'bg-yellow-100 text-yellow-800' :
                          project.status === 'delayed' ? 'bg-red-100 text-red-800' :
                          project.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {project.status?.replace('_', ' ') || "Not Set"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => setPreviewProject(project)}
                          className="p-2 hover:bg-secondary/20 rounded-full transition-colors"
                          title="View details"
                        >
                          <HiOutlineDotsVertical className="w-4 h-4 text-secondary" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-xs text-secondary">
                      <div className="flex flex-col items-center gap-2">
                        <Search className="w-12 h-12 text-gray-300" />
                        <p className="font-semibold text-gray-900">No projects or leads found</p>
                        <p className="text-xs">Try adjusting your filters or search query</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-secondary/20 bg-gray-50">
              <div className="text-xs text-gray-600 order-2 sm:order-1">
                Page <span className="font-semibold text-gray-900">{currentPage}</span> of{" "}
                <span className="font-semibold text-gray-900">{totalPages}</span> •{" "}
                <span className="font-semibold text-gray-900">{sortedProjects.length}</span> total items
              </div>
              
              <div className="flex items-center gap-2 order-1 sm:order-2">
                <Pagination>
                  <PaginationContent className="flex items-center gap-1">
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        className={`cursor-pointer rounded-full border-0 shadow-sm hover:bg-gray-50 transition-all duration-200 ${
                          currentPage === 1 ? "pointer-events-none opacity-30" : ""
                        }`}
                      />
                    </PaginationItem>
                    
                    {/* Dynamic page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            isActive={currentPage === pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`cursor-pointer rounded-full min-w-8 h-8 flex items-center justify-center border-0 transition-all duration-200 text-xs ${
                              currentPage === pageNum 
                                ? "bg-secondary text-white shadow-sm" 
                                : "bg-white shadow-sm hover:bg-secondary/50"
                            }`}
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        className={`cursor-pointer rounded-full border-0 hover:bg-gray-50 transition-all duration-200 text-xs ${
                          currentPage === totalPages ? "pointer-events-none opacity-30" : ""
                        }`}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Blur overlay with working click outside to close */}
      {previewProject && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setPreviewProject(null)}
        >
          <div 
            ref={modalRef}
            className="relative max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <ProjectPreview 
              project={previewProject} 
              onClose={() => setPreviewProject(null)} 
            />
          </div>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          height: 0.5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #FF6900;
          border-radius: 0.25px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #e55a00;
        }
      `}</style>
    </div>
  );
};

export default Stages;